import { LeadOptionsType } from "./CheckoutTypes";
import { UserType } from "./users/UserTypes";
import { AddressType } from "./AddressTypes";
import { ProductVMType } from "./products/ProductCommandsType";

export enum OrderStatusEnum {
  QUOTE = "QUOTE",
  REVIEW = "REVIEW",
  PROCESSING = "PROCESSING",
  // Shipmondo v1: set when the first label is booked or the first part goes
  // out; COMPLETE once every line is sent, collected or handed over.
  SHIPPING = "SHIPPING",
  COMPLETE = "COMPLETE",
  CANCELLED = "CANCELLED",
  UNPAID = "UNPAID",
}

// Fields that may be updated via PUT /orders/<id> (mirrors the backend
// inputmodels/orders/UpdateOrderIM.py). All are optional - only provided
// keys are updated, and unknown keys are rejected with 422. Pass null/""
// to clear delayReason / paymentDueDate.
export type OrderUpdateType = {
  couponCode?: string;
  paymentMethod?: string;
  paymentTermsNumber?: number;
  paymentTermDays?: number;
  paymentDueDate?: string;
  invoiceEmail?: string;
  eanNumber?: string;
  poNumber?: string;
  initials?: string;
  shippingMethod?: string;
  weight?: number;
  delayReason?: string | null; // a DELAY_REASONS code, labels via useDelayReasons
};

export type OrderItemType = {
  id: number;
  created: string;
  modified: string;
  sku: string;
  productId: number;
  product: ProductVMType;
  orderId: number;
  name: string;
  quantity: number;
  unitPrice: number;
  unitTax: number;
  price: number;
  tax: number;
  weight: number;
};

export type SimpleOrderItemType = {
  id: number;
  created: string;
  modified: string;
  sku: string;
  productId: number;
  product: ProductVMType;
  orderId: number;
  name: string;
  quantity: number;
  unitPrice: number;
  unitTax: number;
  price: number;
  tax: number;
  weight: number;
};

export type OrderType = {
  id: number;
  created: string;
  modified: string;
  orderNo: string;
  quoteId: number;
  status: OrderStatusEnum;
  author: UserType;
  firstName: string;
  lastName: string;
  email: string;
  message: string;
  fees: number;
  discount: number;
  shippingTax: number;
  shipping: number;
  subtotalTax: number;
  subtotal: number;
  totalTax: number;
  total: number;
  billingAddress: AddressType;
  shippingAddress: AddressType;
  couponCode?: string;
  paymentMethod: string;
  shippingMethod: string;
  // Shipmondo v1: how it is sent, and the price-table band it was charged
  // from (none for a custom price or pickup).
  deliveryMethod?: "delivery" | "pickup" | "own_delivery" | null;
  isCustomShippingMethod?: boolean;
  shippingPriceColumn?: string | null;
  shippingBandFromKg?: number | null;
  shippingBandToKg?: number | null;
  // Set when the order has a Shipmondo sales order (orders from before v1).
  shipmentCreatedDate?: string | null;
  invoiceId?: string;
  items: OrderItemType[];
  leadTimeOption: LeadOptionsType;
  leadTimeDays: number;
  leadTimeStartDate: string;
  shipmentDate: string;
  weight: number;
  invoiceEmail: string;
  eanNumber: string;
  poNumber: string;
  modified_by: string;
  created_by: string;
  isEanValidChecksum?: boolean | null;
  isEanValidExternal?: boolean | null;
  isEanInvoiceDiff?: boolean | null;
  initials?: string;
  paymentTermsNumber?: number;
  delayReason?: string | null; // a DELAY_REASONS code, labels via useDelayReasons
};

export type SimpleOrderType = {
  id: number;
  created: string;
  modified: string;
  orderNo: string;
  quoteId: number;
  status: OrderStatusEnum;
  authorId: number;
  firstName: string;
  lastName: string;
  email: string;
  fees: number;
  discount: number;
  shippingTax: number;
  shipping: number;
  subtotalTax: number;
  subtotal: number;
  totalTax: number;
  total: number;
  billingAddress: AddressType;
  shippingAddress: AddressType;
  couponCode?: string;
  paymentMethod: string;
  shippingMethod: string;
  deliveryMethod?: "delivery" | "pickup" | "own_delivery" | null;
  invoiceId?: string;
  items: SimpleOrderItemType[];
  itemSummary: {
    cnc: number;
    print3d: number;
    sheet: number;
    other: number;
  };
  leadTimeOption: LeadOptionsType;
  leadTimeDays: number;
  leadTimeStartDate: string;
  shipmentDate: string;
  weight: number;
  invoiceEmail: string;
  eanNumber: string;
  poNumber: string;
  modified_by: string;
  created_by: string;
  isEanValidChecksum?: boolean | null;
  isEanValidExternal?: boolean | null;
  isEanInvoiceDiff?: boolean | null;
  initials?: string;
  delayReason?: string | null; // a DELAY_REASONS code, labels via useDelayReasons
};

export type OrderListType = {
  items: SimpleOrderType[];
  page: number;
  pageSize: number;
  total: number;
};

export type OrderSummaryType = {
  activeOrders: number;
  activeOrdersValue: number;
};
